import { ComponentIds, Entity, Size, Vector } from '@inter-ikea-core/core';
import { CoreApi, Product, ProductType, SlidingDoorComponent } from '@inter-ikea-storageone/range';
import * as R from 'ramda';
import { FrameAndInteriorSuggestions } from './InteriorSuggestionMultipleFrame';
import { CommunicationSizeComponent } from '@inter-ikea-core/core';
import {
  InteriorSuggestionRulesProductsRow,
  InteriorSuggestionRulesRow
} from './InteriorSuggestionsMultiFrameRules';
import { Dimensions } from '@inter-ikea-kvadrat/kvadrat';

/**
 * Filter that list based on categories user have chosen and types of frames etc. Same filters as in single interior suggestions
 * @param coreApi The CoreAPi
 * @param allInteriorSuggestionProducts All interior suggestions products
 * @param interiorSuggestions The suggestions from the rule set.
 * @param framesWithLocation Current frames ordered by location.
 * @returns The ordered list but sets of suggestions that don't match current filters have been filtered out
 */
export const filterInteriorSuggestions = (
  coreApi: CoreApi,
  allInteriorSuggestionProducts: Product[],
  interiorSuggestions: InteriorSuggestionRulesRow[],
  framesWithLocation: FrameWithLocation[]
): InteriorSuggestionRulesProductsRow[] => {
  const suggestions = [] as InteriorSuggestionRulesProductsRow[];
  interiorSuggestions.forEach(suggestionsRow => {
    let shouldAddSet = true;
    const interiorSuggestionsRowProducts = {} as InteriorSuggestionRulesProductsRow;
    Object.keys(suggestionsRow).forEach((width: any) => {
      // widthSet contains a list of suggested interiors. E.g. ['PAX005', 'PAX011']
      const suggestionIds = suggestionsRow[width];
      const framesWithLocationAndRuleSetWidth = framesWithLocation.filter(
        f => f.dimensions.width == width
      );

      interiorSuggestionsRowProducts[width] = [];

      suggestionIds.forEach((suggestionId, i) => {
        const frame = framesWithLocationAndRuleSetWidth[i];

        const interiorSuggestionForFrame = getInteriorSuggestionThatMatchesFrame(
          coreApi,
          allInteriorSuggestionProducts,
          suggestionId,
          frame
        );

        if (interiorSuggestionForFrame) {
          interiorSuggestionsRowProducts[width].push(interiorSuggestionForFrame);
        }
      });

      if (interiorSuggestionsRowProducts[width].length !== suggestionIds.length) {
        shouldAddSet = false;
      }
    });
    if (shouldAddSet) {
      suggestions.push(interiorSuggestionsRowProducts);
    }
  });

  return suggestions;
};

export const filterSuggestionsOnUserFilters = (
  interiorSuggestions: InteriorSuggestionRulesProductsRow[],
  userSelectedFilters: string[][]
): InteriorSuggestionRulesProductsRow[] => {
  if (!userSelectedFilters || userSelectedFilters.length === 0) {
    return interiorSuggestions;
  }

  const filteredInteriorSuggestions = [] as InteriorSuggestionRulesProductsRow[];

  interiorSuggestions.forEach(suggestionsRow => {
    let shouldAddSet = false;
    Object.keys(suggestionsRow).forEach((width: any) => {
      const suggestionsForWidth = suggestionsRow[width];

      suggestionsForWidth.forEach(s => {
        if (suggestionMatchesUserFilters(s, userSelectedFilters)) {
          shouldAddSet = true;
        }
      });
    });

    if (shouldAddSet) {
      filteredInteriorSuggestions.push(suggestionsRow);
    }
  });

  return filteredInteriorSuggestions;
};

export const getInteriorSuggestionThatMatchesFrame = (
  coreApi: CoreApi,
  allInteriorSuggestionProducts: Product[],
  interiorSuggestionId: string, // E.g 'PAX001'
  frameWithLocation: FrameWithLocation
): Product | undefined => {
  return allInteriorSuggestionProducts.filter(
    is =>
      is.coreProductId.startsWith(interiorSuggestionId) &&
      R.equals(is.communicatedDimensions, frameWithLocation.dimensions) &&
      matchesDoorType(coreApi, is, frameWithLocation.frame)
  )[0];
};

export const createFakeProductsForFramesAndSuggestionSet = (
  framesWithLocation: FrameWithLocation[],
  interiorSuggestionsRow: InteriorSuggestionRulesProductsRow
): FrameAndInteriorSuggestions[] => {
  const framesAndInteriorSuggestions = [] as FrameAndInteriorSuggestions[];

  framesWithLocation.forEach(f => {
    const suggestion = interiorSuggestionsRow[f.dimensions.width].shift();

    if (suggestion) {
      framesAndInteriorSuggestions.push({
        suggestion,
        ...f
      });
    }
  });

  return framesAndInteriorSuggestions;
};

export const suggestionMatchesUserFilters = (suggestion: Product, userSelectedFilters: any[][]) => {
  const partTypes = suggestion.parts.flatMap(part => part.product?.type || []);
  const allOptionsSatisfied = userSelectedFilters.every(selectedTypes =>
    selectedTypes.find(type => partTypes.includes(type))
  );
  return allOptionsSatisfied;
};

export const matchesDoorType = (coreApi: CoreApi, interiorSuggestion: Product, frame: Entity) => {
  const hingedDoorsOnFrame = coreApi.entities
    .getFrameDescendants(frame)
    .filter(
      entity =>
        entity.hasComponent(ComponentIds.DOOR_COMPONENT_ID) &&
        !entity.hasComponent(SlidingDoorComponent.id)
    );

  if (!interiorSuggestion.meta || !interiorSuggestion.meta.applicableFrameTypes) {
    return true;
  }

  const hasHinged = interiorSuggestion.meta.applicableFrameTypes.includes('hinged');
  const hasNonHinged = interiorSuggestion.meta.applicableFrameTypes.includes('non-hinged');

  if (hasHinged && hasNonHinged) {
    return true;
  }

  return hingedDoorsOnFrame.length > 0 ? hasHinged : hasNonHinged;
};
export interface FrameWithLocation {
  frame: Entity;
  worldPosition: Vector;
  dimensions: Dimensions;
}

export const sortFramesByXPosition = (coreApi: CoreApi, frames: Entity[]): FrameWithLocation[] => {
  const framesWithPosition = frames.map(f => {
    const worldPosition = coreApi.entities.getWorldPosition(f);
    const dimensions =
      f.getComponent<CommunicationSizeComponent>(ComponentIds.COMMUNICATION_SIZE_COMPONENT_ID)
        ?.size || new Size(0, 0, 0);

    return {
      frame: f,
      worldPosition,
      dimensions: { width: dimensions.width, height: dimensions.height, depth: dimensions.depth }
    };
  });

  return framesWithPosition.sort((a, b) => a.worldPosition.X - b.worldPosition.X);
};

export const getSelectedFilters = (filterState: any, selectedGroupId: string) => {
  const optionsMap: Record<string, ProductType[]> = filterState(selectedGroupId, 'type');
  return Object.values(optionsMap).filter(o => o.length > 0);
};
